import { ApiRequest, getStoreConfiguration, ShowLoader } from 'GlobalFunctions';
import { useEffect, useState, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import GenerateVariants from './Variants';
import getDataAttributes from './Attributes';
import { showBag, getProductQuantity } from 'components/Bag/Bag';
import FileUploader from './FileUploader';
import { checkForSubscription, startSubscription, cleanupSubscription } from '../Subscription/SubscriptionUtils';
import { getTaxes } from '../Subscription/Subscription';
import { enqueueSnackbar } from "notistack";

// Keep roots map outside component to prevent recreation
const fileUploaderRoots = new Map();

const sortBundleProducts = (bundle: any[]): any[] => {
    return [...(bundle || [])].sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        if (a.price < b.price) return -1;
        if (a.price > b.price) return 1;
        if (a.image < b.image) return -1;
        if (a.image > b.image) return 1;
        return 0;
    });
};

export default function ProductUtils() {
    const [fileUploads, setFileUploads] = useState<{ [key: string]: { files: File[] } }>({});
    const fileUploadsRef = useRef(fileUploads); // Add this line to keep a reference
    const isMounted = useRef(true);
    const [files, setFiles] = useState<File[]>([]); // Modified to explicitly type as File array
    const filesRef = useRef<File[]>([]);

    const [formValues, setFormValues] = useState({});
    
    // Update ref when state changes
    useEffect(() => {
        fileUploadsRef.current = fileUploads;
    }, [fileUploads]);

    // Add this effect to keep ref updated
    useEffect(() => {
        filesRef.current = files;
    }, [files]);


    /*=============================================
    VALIDATE REQUIRED FIELDS
    =============================================*/
    const validateRequiredFields = () => {
        const requiredFields = [
            { selector: '[pc="product-name"]', name: 'Product Name' },
            { selector: '[pc="product-price"]', name: 'Product Price' },
            { selector: '[pc="product-quantity"]', name: 'Product Quantity' },
            { selector: '[pc="form"]', name: 'Product Form' },
        ];

        const missingFields: string[] = [];

        // Check basic required fields
        requiredFields.forEach(({ selector, name }) => {
            const element = document.querySelector(selector);
            if (!element) {
                missingFields.push(name);
            }
        });

        // Check for subscription form
        const subscriptionForm = document.querySelector('[pc-sub="form"]');
        const isSubscription = !!subscriptionForm;
        
        if (isSubscription) {
            const subscriptionMode = subscriptionForm?.getAttribute('pc-sub-mode') || 'modal';
            
            // For modal mode, add-btn is required
            if (subscriptionMode === 'modal') {
                const addBtn = document.querySelector('[pc="add-btn"]');
                if (!addBtn) {
                    missingFields.push('Add Button');
                }
            }
            
            // For subscription products, check required subscription fields
            const subValue = document.querySelector('[pc-sub-value]');
            const subElement = document.querySelector('[pc-sub-element]');
            
            if (!subValue) {
                missingFields.push('Subscription Value');
            }
            if (!subElement) {
                missingFields.push('Subscription Element');
            }
        } else {
            // For regular products, add-btn is always required
            const addBtn = document.querySelector('[pc="add-btn"]');
            if (!addBtn) {
                missingFields.push('Add Button');
            }
        }

        // Show error toast if there are missing fields
        if (missingFields.length > 0) {
            const errorMessage = `Missing required fields: ${missingFields.join(', ')}`;
            showErrorToast(errorMessage);
            return false;
        }

        return true;
    };

    /*=============================================
    SHOW ERROR TOAST
    =============================================*/
    const showErrorToast = (message: string) => {
        enqueueSnackbar(message, { 
            variant: 'error',
            autoHideDuration: 5000,
        });
        console.error(message);
    };

    /*=============================================
    CLEAR BUNDLE PRODUCT CHECKBOXES
    =============================================*/
    const clearBundleProductCheckboxes = () => {
        const bundleCheckboxes = document.querySelectorAll('[pc="bundle-product-checkbox"]') as NodeListOf<HTMLInputElement>;
        bundleCheckboxes.forEach(checkbox => {
            checkbox.checked = false;
        });
    };

    const init = async () => {

        // Set mounted flag
        isMounted.current = true;

        // Clear bundle product checkboxes on initialization to prevent cached states
        clearBundleProductCheckboxes();

        // Validate required fields first
        if (!validateRequiredFields()) {
            return; // Stop initialization if validation fails
        }

        // Store roots for cleanup
        const roots = new Map();
        
        const selectors = [
            { attribute: "product-name" },
            { attribute: "product-price" },
            { attribute: "product-quantity" },
            { attribute: "product-sku" },
            { attribute: "product-image", target: "src" },
            { attribute: "tax-type" },
            { attribute: "weight" },
            { attribute: "length" },
            { attribute: "width" },
            { attribute: "height" },
            { attribute: "category" },
            { attribute: "bulk-tiered-discounts" },
            { attribute: "v1-select" },
            { attribute: "v2-select" },
        ];

        // Check for subscription early and determine mode
        const subscriptionFormEl = document.querySelector('[pc-sub="form"]');
        const subscriptionMode = subscriptionFormEl?.getAttribute('pc-sub-mode') || 'modal';
        const isSubscription = !!subscriptionFormEl;
        const isEmbedMode = isSubscription && subscriptionMode === 'embed';


        if (isSubscription) {
            await checkForSubscription();
            if (isEmbedMode) {
                const initializeEmbedSubscription = async () => {
                    const firstForm = document.querySelector("[pc='form']");
                    const productData = await getDataAttributes(selectors, firstForm as HTMLElement);
                    
                    if (firstForm) {
                        productData.custom_inputs = await getCustomInputs(firstForm as HTMLFormElement);
                    }

                    // Add extra fields to custom_inputs if they exist in productData
                    const extraFields = [
                        { key: 'product_sku', name: 'sku' },
                        { key: 'weight', name: 'weight' },
                        { key: 'length', name: 'length' },
                        { key: 'width', name: 'width' },
                        { key: 'height', name: 'height' },
                        { key: 'category', name: 'category' },
                    ];
                    if (!productData.custom_inputs) productData.custom_inputs = [];
                    extraFields.forEach(({ key, name }) => {
                        if (productData[key]) {
                            productData.custom_inputs.push({ name, value: productData[key] });
                        }
                    });
                    
                    startSubscription(productData);
                };

                initializeEmbedSubscription();
            }
        }

        /*=============================================
        GET ALL INPUTS VALUES INSIDE ADD TO CART FORM 
        =============================================*/
        const getCustomInputs = async (form: HTMLFormElement) => {
            // Helper function to check if an element is inside a bundle item
            const isInsideBundleItem = (element: HTMLElement) => {
                let current = element.parentElement;
                while (current) {
                    if (current.hasAttribute('pc-bundle') && current.getAttribute('pc-bundle') === 'item') {
                        return true;
                    }
                    current = current.parentElement;
                }
                return false;
            };

            // Get inputs from form elements excluding those with pc/pc-sub attributes and those inside bundle items
            const customInputs = Array.from(form.elements)
                .filter((element: any) => 
                    !element.hasAttribute('pc') &&
                    !element.hasAttribute('pc-sub') &&
                    !element.hasAttribute('pc-sub-element') &&
                    !element.hasAttribute('pc-sub-value') &&
                    !isInsideBundleItem(element)
                )
                .map((element: any) => {
                    let value;
                    if (element.type === 'checkbox') {
                        if (!element.checked) return null;
                        value = element.checked;
                    } else if (element.type === 'radio') {
                        if (element.checked) {
                            value = element.value;
                        } else {
                            return null;
                        }
                    } else {
                        value = element.value;
                    }
                    // Exclude inputs with name 'image'
                    if (element.name === 'image') return null;
                    return {    
                        name: element.name,
                        value: value
                    };
                })
                .filter(input => input !== null);
        
            // Capture elements with pc-custom attribute (excluding those inside bundle items)
            const pcCustomElements = Array.from(form.querySelectorAll('[pc-custom]'))
                .filter((element) => !isInsideBundleItem(element as HTMLElement))
                .map((element) => {
                    const htmlElement = element as HTMLElement; // Ensure the element is treated as HTMLElement
                    const name = htmlElement.getAttribute('pc-custom');
                    const value = htmlElement.innerText.trim(); // The text inside the tag will be the value
                    return {
                        name: name,
                        value: value
                    };
                });
        
            const variantAttributes = getVariantAttributes(form);
        
            // Combine all inputs (form inputs + pc-custom elements + variant attributes)
            return [...customInputs, ...pcCustomElements, ...variantAttributes];
        };

        /*=============================================
        SETUP EMBED MODE LISTENERS
        =============================================*/
        const setupEmbedModeListeners = (form: HTMLElement) => {
            if (!form) return;

            // Function to update product data in localStorage for embed mode
            const updateEmbedProductData = async () => {
                try {
                    const productData = await getDataAttributes(selectors, form);
                    
                    if (form) {
                        productData.custom_inputs = await getCustomInputs(form as HTMLFormElement);
                    }

                    // Add extra fields to custom_inputs if they exist in productData
                    const extraFields = [
                        { key: 'product_sku', name: 'sku' },
                        { key: 'weight', name: 'weight' },
                        { key: 'length', name: 'length' },
                        { key: 'width', name: 'width' },
                        { key: 'height', name: 'height' },
                        { key: 'category', name: 'category' },
                    ];
                    if (!productData.custom_inputs) productData.custom_inputs = [];
                    extraFields.forEach(({ key, name }) => {
                        if (productData[key]) {
                            productData.custom_inputs.push({ name, value: productData[key] });
                        }
                    });

                    // Update subscription data in localStorage
                    const storedData = localStorage.getItem('subscription');
                    if (storedData) {
                        const subscriptionData = JSON.parse(storedData);
                        
                        // Update the product data in subscription
                        if (subscriptionData.products && subscriptionData.products[0]) {
                            // Keep the billingCycle from the current subscription data
                            const currentBillingCycle = subscriptionData.products[0].billingCycle;
                            subscriptionData.products[0] = { ...productData };
                            if (currentBillingCycle) {
                                subscriptionData.products[0].billingCycle = currentBillingCycle;
                            }
                        }
                        
                        // Update totals based on new product data
                        const currentPrice = parseFloat(productData.product_price) || 0;
                        subscriptionData.subtotal = currentPrice;
                        subscriptionData.total_price = currentPrice;
                        
                        localStorage.setItem('subscription', JSON.stringify(subscriptionData));
                        
                        console.log('Updated embed subscription data:', subscriptionData);
                        
                        // Recalculate taxes if address data is complete
                        if (subscriptionData.client &&
                            subscriptionData.client.country &&
                            subscriptionData.client.state &&
                            subscriptionData.client.city &&
                            subscriptionData.client.postal) {
                            try {
                                await getTaxes();
                            } catch (error) {
                                console.error('Error recalculating taxes:', error);
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error updating embed product data:', error);
                }
            };

            // Debounced version to avoid too many updates
            let updateTimeout: NodeJS.Timeout;
            const debouncedUpdate = () => {
                clearTimeout(updateTimeout);
                updateTimeout = setTimeout(updateEmbedProductData, 300);
            };

            // Add listeners to all form inputs
            const formInputs = form.querySelectorAll('input, select, textarea');
            formInputs.forEach(input => {
                // Skip file inputs as they are handled differently
                if (input instanceof HTMLInputElement && input.type === 'file') return;
                
                input.addEventListener('change', debouncedUpdate);
                input.addEventListener('input', debouncedUpdate);
            });

            // Add listeners to variant selectors specifically
            const variantSelectors = ['v1-select', 'v2-select'];
            variantSelectors.forEach(selector => {
                const elements = form.querySelectorAll(`[pc='${selector}']`);
                elements.forEach(element => {
                    element.addEventListener('change', debouncedUpdate);
                });
            });

            // Add listeners to quantity inputs specifically
            const quantityInputs = form.querySelectorAll("[pc='quantity']");
            quantityInputs.forEach(input => {
                input.addEventListener('input', debouncedUpdate);
                input.addEventListener('change', debouncedUpdate);
            });

            // Add listeners to custom inputs that might not have pc attributes
            const customInputs = form.querySelectorAll('input:not([pc]), select:not([pc]), textarea:not([pc])');
            customInputs.forEach(input => {
                if (input instanceof HTMLInputElement && input.type === 'file') return;
                
                input.addEventListener('change', debouncedUpdate);
                input.addEventListener('input', debouncedUpdate);
            });
        };

        // Call setupEmbedModeListeners if in embed mode
        if (isEmbedMode) {
            const firstForm = document.querySelector("[pc='form']");
            if (firstForm) {
                setupEmbedModeListeners(firstForm as HTMLElement);
            }
        }

        /*=============================================
        GET VARIANT ATTRIBUTES
        =============================================*/
        const getVariantAttributes = (form: HTMLElement) => {
            const variantAttributes: { name: string, value: string }[] = [];
            const variantSelectors = ["v1-select", "v2-select"];

            variantSelectors.forEach((selector, index) => {
                const inputs = form.querySelectorAll(`[pc='${selector}']`) as NodeListOf<HTMLInputElement>;
                inputs.forEach((input, i) => {
                    const value = input.value;
                    const name = input.getAttribute('name') || `custom_${index + 1}_${i + 1}`;
                    variantAttributes.push({ name, value });
                });
            });

            return variantAttributes;
        }

        const validateVariants = (form: HTMLElement) => {
            const variantSelectors = ["v1-select", "v2-select"];
            for (const selector of variantSelectors) {
                const inputs = form.querySelectorAll(`[pc='${selector}']`) as NodeListOf<HTMLInputElement>;
            }
            return true;
        }

        /*=============================================
        GET SELECTED BUNDLE PRODUCTS
        =============================================*/
        const getSelectedBundleProducts = (form: HTMLElement) => {
            const bundleElements = form.querySelectorAll('[pc-bundle="item"]');
            let bundle_products: any[] = [];
            bundleElements.forEach((bundleEl) => {
                const checkbox = bundleEl.querySelector('[pc-bundle="checkbox"]') as HTMLInputElement;
                if (checkbox && checkbox.checked) {
                    // Use standard product attributes instead of bundle-specific ones
                    const nameEl = bundleEl.querySelector('[pc="product-name"]');
                    const skuEl = bundleEl.querySelector('[pc="product-sku"]');
                    const priceEl = bundleEl.querySelector('[pc="product-price"]');
                    const imageEl = bundleEl.querySelector('[pc="product-image"]');
                    const quantityEl = bundleEl.querySelector('[pc="product-quantity"]') as HTMLInputElement;
                    
                    // Extract price value (remove $ and any formatting)
                    const priceText = priceEl ? priceEl.textContent || '' : '';
                    const priceValue = priceText.replace(/[$,]/g, '');
                    
                    // Extract quantity value, default to 1 if not found or invalid
                    const quantityValue = quantityEl ? parseInt(quantityEl.value) || 1 : 1;
                    
                    bundle_products.push({
                        id: `bundle-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`, // Generate unique ID
                        name: nameEl ? nameEl.textContent || '' : '',
                        sku: skuEl ? skuEl.textContent || '' : '',
                        price: priceValue,
                        image: imageEl ? imageEl.getAttribute('src') || '' : '',
                        quantity: quantityValue
                    });
                }
            });

            return bundle_products;
        }

        /*=============================================
        CONFIGURE ADD TO CART BUTTONS FOR ALL FORMS
        =============================================*/
        // Find all forms and configure each one
        const allForms = document.querySelectorAll("[pc='form']");
        
        
        allForms.forEach((formElement, index) => {
            const form = formElement as HTMLElement;
            const addToCartBtn = form.querySelector("[pc='add-btn']");
            
            // Try to find the product container (new structure with pc="product")
            const productContainer = form.closest("[pc='product']") as HTMLElement;
            

            
            if (addToCartBtn && !isEmbedMode) {
                // Prevent default link behavior if it's an anchor tag
                if (addToCartBtn.tagName === 'A') {
                    addToCartBtn.addEventListener("click", (e) => {
                        e.preventDefault();
                    });
                }
                
                addToCartBtn.addEventListener("click", async (e) => {
                    e.preventDefault();
                    
                    // Determine context element:
                    // 1. If pc="product" container exists, use it (new structure)
                    // 2. Otherwise, use form normally (each form is still independent)
                    const contextElement = productContainer || form;
                    
                    // For legacy support, we'll pass undefined to getDataAttributes if no product container
                    // This makes it search in document (first match) like the old behavior
                    const searchContext = productContainer ? contextElement : undefined;
                    
                    
                    // Pre-fill quantity input if empty
                    const quantityInput = contextElement.querySelector("[pc='product-quantity']") as HTMLInputElement;
                    if (quantityInput && !quantityInput.value) {
                        quantityInput.value = "1";
                    }
                    
                    const requiredInputs = form.querySelectorAll("[required]") as NodeListOf<HTMLInputElement>;
                    
                    let allRequiredFilled = true;
                    const missingFields: string[] = [];

                    requiredInputs?.forEach(input => {
                        let isValid = true;
                        
                        // Special handling for quantity inputs - set to 1 if empty
                        if (input.getAttribute('pc') === 'product-quantity' && !input.value) {
                            input.value = "1";
                        }
                        
                        if (input.type === 'checkbox' || input.type === 'radio') {
                            // Para checkbox y radio, verificar si alguno del mismo nombre está checked
                            const name = input.getAttribute('name');
                            const groupInputs = form.querySelectorAll(`[name="${name}"]`) as NodeListOf<HTMLInputElement>;
                            isValid = Array.from(groupInputs).some(inp => inp.checked);
                        } else {
                            // Para otros tipos de inputs, verificar el valor
                            isValid = input.value.trim() !== '';
                        }

                        if (!isValid) {
                            allRequiredFilled = false;
                            input.classList.add('error');
                            missingFields.push(`${input.name || input.id || input.type} (value: "${input.value}")`);
                        } else {
                            input.classList.remove('error');
                        }
                    });

                    if (!allRequiredFilled) {
                        return;
                    }

                    if (!validateVariants(searchContext || form)) {
                        return;
                    }
                    
                    const productData = await getDataAttributes(selectors, searchContext);
                    
                    if (form) {
                        productData.custom_inputs = await getCustomInputs(form as HTMLFormElement);
                    }

                    // Añadir valores extra a custom_inputs si existen en productData
                    const extraFields = [
                        { key: 'product_sku', name: 'sku' },
                        { key: 'weight', name: 'weight' },
                        { key: 'length', name: 'length' },
                        { key: 'width', name: 'width' },
                        { key: 'height', name: 'height' },
                        { key: 'category', name: 'category' },
                    ];
                    if (!productData.custom_inputs) productData.custom_inputs = [];
                    extraFields.forEach(({ key, name }) => {
                        if (productData[key]) {
                            productData.custom_inputs.push({ name, value: productData[key] });
                        }
                    });

                    /*=========================================
                    CHECK IF PRODUCT IS SUBSCRIPTION (Modal mode only)
                    =========================================*/
                    if (isSubscription) {
                        // Call startSubscription for modal mode
                        // Pass contextElement so it reads data from the correct product container
                        startSubscription(productData, contextElement);
                    } else {
                        const storeConfiguration: any = await getStoreConfiguration(); 
                        const is_bundle_products_active = storeConfiguration.logic?.build_bundle?.is_active || false;
                        
                        if (is_bundle_products_active) {
                            const selectedBundles = getSelectedBundleProducts(searchContext || form);
                            if (selectedBundles.length > 0) {
                                productData.bundle_products = selectedBundles;
                            }
                        } 
                        await AddToCart(productData);
                    }
                });
            }
        });

        // Modified file upload initialization
        const initializeFileUploader = () => {
            const fileElements = document.querySelectorAll("[pc='file']");
            fileElements.forEach((element, index) => {
                const uploaderId = `fileUploader_${index}`;
                if (fileUploaderRoots.has(uploaderId)) return;

                // Read the pc-upload attribute or default to 1
                const maxFiles = parseInt(element.getAttribute('pc-upload') || '1', 10);

                const fileContainer = document.createElement('div');
                fileContainer.className = 'pc-file-uploader-container';

                const handleFileChange = (files: File[]) => {
                    if (isMounted.current) {
                        setFileUploads(prev => {
                            const newState = {
                                ...prev,
                                [uploaderId]: { files }
                            };
                            console.log("ProductUtils - Setting new state:", newState);
                            return newState;
                        });
                    }
                };

                const root = createRoot(fileContainer);
                fileUploaderRoots.set(uploaderId, root);

                root.render(
                    <FileUploader 
                        className="account_logo"
                        image={""} 
                        setImage={(newImage) => setFormValues({ ...formValues, file: newImage })}
                        setFiles={setFiles}
                        maxFiles={maxFiles}
                    />
                );

                element.parentNode?.replaceChild(fileContainer, element);
            });
        };

        initializeFileUploader();
        
        // Updated cleanup function
        return () => {
            isMounted.current = false;
            
            // Clean up subscription elements
            cleanupSubscription();
            
            // Delay unmounting to next tick to avoid React rendering conflicts
            fileUploaderRoots.forEach((root, key) => {
                try {
                    root.unmount();
                    fileUploaderRoots.delete(key);
                } catch (error) {
                    console.warn('Error unmounting file uploader:', error);
                }
            });
        };
    }

    useEffect(() => {
        init();
    }, []);

    GenerateVariants();

    useEffect(() => {
        handleQuantityInput()
    }, []);


    const handleQuantityInput = async () => {

        const storeConfiguration: any = await getStoreConfiguration();
        const quantityInput = document.querySelector("[pc='product-quantity']") as HTMLInputElement;
        const priceElement = document.querySelector("[pc='product-price']") as HTMLInputElement;
        const discountElement = document.querySelector("[pc='bulk-tiered-discounts']") as HTMLDivElement;
        
        // Check if we're in embed subscription mode
        const subscriptionFormEl = document.querySelector('[pc-sub="form"]');
        const subscriptionMode = subscriptionFormEl?.getAttribute('pc-sub-mode') || 'modal';
        const isSubscription = !!subscriptionFormEl;
        const isEmbedMode = isSubscription && subscriptionMode === 'embed';
        
        // Verificar si el descuento "bulk_tiered_discounts" está activo en storeConfiguration usando `any` en `config`
        // En modo embed, siempre establecer bulkTieredDiscountConfig como false
        const bulkTieredDiscountConfig = isEmbedMode ? false : (storeConfiguration.logic?.bulk_tiered_discounts?.is_active || false);
        if (quantityInput) {
            let basePrice = priceElement ? parseFloat(priceElement.innerText) || 0 : 0;
    
            let discounts =
                bulkTieredDiscountConfig && discountElement
                    ? discountElement.innerText
                          .split(',')
                          .map((discount) => {
                              const [price, discount_quantity] = discount
                                  .split(':')
                                  .map((item) => parseFloat(item));
                              return { price, discount_quantity };
                          })
                          .sort((a, b) => a.discount_quantity - b.discount_quantity)
                    : []; // Ordenar por cantidad ascendente o mantener vacío
            
            // Check if there's a discount for quantity 1, if not add it with base price
            if (bulkTieredDiscountConfig && discounts.length > 0) {
                const hasQuantityOne = discounts.some(d => d.discount_quantity === 1);
                if (!hasQuantityOne) {
                    discounts = [{ price: basePrice, discount_quantity: 1 }, ...discounts];
                    
                    // Update the discount element with the new array that includes quantity 1
                    if (discountElement) {
                        const updatedDiscountString = discounts
                            .map(d => `${d.price}:${d.discount_quantity}`)
                            .join(',');
                        discountElement.innerText = updatedDiscountString;
                    }
                }
            }
    
            const updatePriceBasedOnQuantity = (quantity: number) => {
                if (bulkTieredDiscountConfig && priceElement && discounts.length > 0) {
                    let newPrice = basePrice;
    
                    for (const discount of discounts) {
                        if (quantity >= discount.discount_quantity) {
                            newPrice = discount.price;
                        } else {
                            break;
                        }
                    }
    
                    priceElement.innerText = newPrice.toLocaleString('en-US', { minimumFractionDigits: 2 });
                }
            };
    
            const validateAndSetQuantity = () => {
                let value = parseInt(quantityInput.value);
                if (isNaN(value) || value < 1) {
                    quantityInput.value = '1';
                    value = 1;
                }
                updatePriceBasedOnQuantity(value);
            };
    
            quantityInput.addEventListener("input", () => {
                // Permitir edición libre sin validar inmediatamente
                const value = parseInt(quantityInput.value);
                if (!isNaN(value) && value >= 1) {
                    updatePriceBasedOnQuantity(value);
                }
            });
    
            quantityInput.addEventListener("blur", () => {
                // Validar y ajustar el valor cuando se pierde el foco
                validateAndSetQuantity();
            });
    
            // Validar el input al cargar la página
            validateAndSetQuantity();
        }
    };



    /*=========================================================
    ADD TO CART
    =========================================================*/
    const AddToCart = async (productData: any) => {
        const body = new FormData();
        // Now filesRef.current is guaranteed to be an array
        if (filesRef.current && filesRef.current.length > 0) {
            ShowLoader(true);
            filesRef.current.forEach((file: File, index: number) => {
                body.append(`files`, file); // Changed to use 'files' as key
            });
            
            await ApiRequest({
                url: "/orders/uploadFilesToCart",
                method: "post",
                body: body,
                formData: true,
                setResponse: async (response: any) => {
                    if(response.status === "success") {
                        productData.custom_inputs.push(response.files);
                    }
                },
            });
            ShowLoader(false);
        }
        try {
            // Get existing cart or initialize with products array
            const existingCart = JSON.parse(localStorage.getItem('cart') || '{"products":[]}');
            
            // Ensure cart has products array
            if (!existingCart.products) {
                existingCart.products = [];
            }
            
            // Add order_code if it doesn't exist
            if (!existingCart.order_code) {
                existingCart.order_code = `PC-${Date.now()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
            }

            // Ensure price and quantity are formatted as numbers using Number constructor
            if (productData.product_price) {
                productData.product_price = Number(productData.product_price) || 0;
            }
            if (productData.product_quantity) {
                productData.product_quantity = Number(productData.product_quantity) || 1;
            }

            // Helper to sort custom_inputs for comparison
            const sortCustomInputs = (inputs: any[]) => {
                return [...(inputs || [])].sort((a, b) => {
                    if (a.name < b.name) return -1;
                    if (a.name > b.name) return 1;
                    if (a.value < b.value) return -1;
                    if (a.value > b.value) return 1;
                    return 0;
                });
            };

            // Find if product already exists in cart
            // Products are considered the same if they have:
            // - Same SKU (if SKU exists)
            // - Same name
            // - Same price
            // - Same custom_inputs
            // - Same bundle_products
            const existingIndex = existingCart.products.findIndex((item: any) => {
                // Compare SKU if both products have SKU
                if (item.product_sku && productData.product_sku) {
                    if (item.product_sku !== productData.product_sku) return false;
                } else {
                    // If no SKU, compare by name and price to differentiate products
                    if (item.product_name !== productData.product_name) return false;
                    if (Number(item.product_price) !== Number(productData.product_price)) return false;
                }
                
                // Compare custom inputs
                const a = sortCustomInputs(item.custom_inputs || []);
                const b = sortCustomInputs(productData.custom_inputs || []);
                if (JSON.stringify(a) !== JSON.stringify(b)) return false;
                
                // Compare bundle products
                if (
                    JSON.stringify(sortBundleProducts(item.bundle_products || [])) !==
                    JSON.stringify(sortBundleProducts(productData.bundle_products || []))
                ) {
                    return false;
                }
                
                return true;
            });
            


            if (existingIndex !== -1) {
                // Product exists: sum quantity
                const existingItem = existingCart.products[existingIndex];
                existingItem.product_quantity = Number(existingItem.product_quantity || 1) + Number(productData.product_quantity || 1);
                existingItem.timestamp = new Date().toISOString();
            } else {
                // Product does not exist: add new
                const itemId = Date.now().toString();
                const cartItem = {
                    order_product_id: itemId,
                    ...productData,
                    timestamp: new Date().toISOString(),
                };
                existingCart.products.push(cartItem);
            }

            localStorage.setItem('cart', JSON.stringify(existingCart));

            //trigger zapier event
            ApiRequest({
                url: "/orders/addToCart",
                method: "post",
                body: {
                    cart: existingCart,
                }
            });
            // Update UI
            await getProductQuantity();
            showBag("add");
        } catch (error) {
            console.error('Error storing cart in localStorage:', error);
            alert('Failed to add product to cart');
        }
    };

    return null;
}



